import { X } from 'lucide-react'
import { Button, Chip, Drawer } from '@/components/ui'
import { cn } from '@/lib/utils/cn'
import type { CustomerOrderHistoryEntry, LookupDirection, PricingRow } from '../api/types'
import { CostTrendChip, MarginCell, TrendDeltaChip, isCustomerRow, pricingRowId } from './PricingRowHelpers'
import { Sparkline } from './Sparkline'

type Unit = 'kg' | 'lb'

export interface CompareDrawerProps {
  /** The full row objects, already in memory from the page's own query
   *  result (2–4 of them) — this drawer is purely presentational and
   *  issues no network request of its own. Empty array closes the drawer. */
  rows: PricingRow[]
  direction: LookupDirection
  unit: Unit
  onClose: () => void
  /** Optional — drops one entity from the comparison without closing the
   *  drawer (and keeps the page's row-selection state in sync). */
  onRemove?: (id: string) => void
}

/**
 * Side-by-side comparison of 2–4 selected pricing rows — entities as
 * columns, metrics as rows (a transposed grid, not another flat table).
 * Row order mirrors the main DataTable's column order so a customer who
 * already knows that table can read this one without re-orienting.
 */
export function CompareDrawer({ rows, direction, unit, onClose, onRemove }: CompareDrawerProps) {
  const open = rows.length > 0
  const columnCount = Math.max(rows.length, 1)
  const gridStyle: React.CSSProperties = {
    gridTemplateColumns: `160px repeat(${columnCount}, minmax(0, 1fr))`,
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      size="xl"
      title="Compare"
      meta={open ? <span>{rows.length} selected</span> : undefined}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {!open ? null : (
        <div className="overflow-x-auto">
          <div className="grid gap-x-4" style={gridStyle}>
            {/* Corner cell above the metric-label column. */}
            <div className="border-b border-border-default pb-2" />
            {rows.map((row) => (
              <ColumnHeaderCell key={pricingRowId(row)} row={row} onRemove={onRemove} />
            ))}

            <MetricRow label="Last order" cells={rows.map((r) => fmtDate(r.last_order_date))} />
            <MetricRow label="Qty (last order)" cells={rows.map((r) => fmtQty(r.qty_last))} />
            <MetricRow
              label={`Price / ${unit}`}
              cells={rows.map((r) => priceCell(r, unit))}
            />
            <MetricRow label={`Cost / ${unit}`} cells={rows.map((r) => costCell(r, unit))} />
            <MetricRow
              label="Margin % (last order)"
              cells={rows.map((r) => <MarginCell value={r.true_margin_pct_last} />)}
            />
            <MetricRow label="Margin trend" cells={rows.map((r) => marginTrendCell(r))} />
            <MetricRow label="Cost Trend (YoY)" cells={rows.map((r) => costTrendCell(r))} />
            <MetricRow
              label="Dedicated pricelist"
              cells={rows.map((r) => (
                <Chip size="xs" variant={r.has_dedicated_pricelist ? 'success' : 'neutral'}>
                  {r.has_dedicated_pricelist ? 'Yes' : 'No'}
                </Chip>
              ))}
            />
            <MetricRow
              label="Margin floor"
              cells={rows.map((r) =>
                r.true_margin_pct_last === null ? (
                  <Chip size="xs" variant="neutral">
                    —
                  </Chip>
                ) : (
                  <Chip size="xs" variant={r.below_margin_floor ? 'danger' : 'success'}>
                    {r.below_margin_floor ? 'Below floor' : 'At/above floor'}
                  </Chip>
                ),
              )}
            />
            {direction === 'by-product' ? (
              <>
                <MetricRow
                  label="Booked $"
                  cells={rows.map((r) => (isCustomerRow(r) ? fmtMoney(r.ordered_revenue) : '—'))}
                />
                <MetricRow
                  label="Booked %"
                  cells={rows.map((r) => (isCustomerRow(r) ? <MarginCell value={r.ordered_margin_pct} /> : '—'))}
                />
              </>
            ) : null}
            <MetricRow label="Orders" cells={rows.map((r) => r.orders_count_in_window.toLocaleString())} />
            <MetricRow
              label="Revenue"
              cells={rows.map((r) => fmtMoney(r.total_revenue_in_window))}
              isLast
            />
          </div>
        </div>
      )}
    </Drawer>
  )
}

function ColumnHeaderCell({ row, onRemove }: { row: PricingRow; onRemove?: (id: string) => void }) {
  const id = pricingRowId(row)
  const name = isCustomerRow(row) ? row.customer_name : row.product_name
  const code = !isCustomerRow(row) ? row.default_code : null
  return (
    <div className="flex items-start justify-between gap-2 border-b border-border-default pb-2">
      <div className="min-w-0">
        <div className="truncate text-role-data-primary">{name ?? '—'}</div>
        {code ? <div className="text-role-metadata text-tertiary">{code}</div> : null}
      </div>
      {onRemove ? (
        <button
          type="button"
          aria-label={`Remove ${name ?? 'row'} from comparison`}
          onClick={() => onRemove(id)}
          className="shrink-0 cursor-pointer rounded-xs border-none bg-transparent p-0.5 text-tertiary hover:bg-surface-hover hover:text-primary"
        >
          <X size={12} />
        </button>
      ) : null}
    </div>
  )
}

function MetricRow({
  label,
  cells,
  isLast = false,
}: {
  label: string
  cells: React.ReactNode[]
  isLast?: boolean
}) {
  const cellCls = cn('px-1 py-2 text-sm text-primary', !isLast && 'border-b border-border-subtle')
  return (
    <>
      <div className={cn(cellCls, 'text-role-metadata text-tertiary')}>{label}</div>
      {cells.map((c, i) => (
        <div key={i} className={cellCls}>
          {c}
        </div>
      ))}
    </>
  )
}

function marginTrendCell(row: PricingRow) {
  const hist: CustomerOrderHistoryEntry[] = row.history ?? []
  if (hist.length < 2) {
    return <span className="text-role-metadata text-tertiary">Not enough data</span>
  }
  const points = hist.slice(-12).map((h) => ({ date: h.invoice_date ?? '', value: h.true_margin_pct ?? 0 }))
  const label = isCustomerRow(row) ? row.customer_name : row.product_name
  const trend = row.margin_trend
  return (
    <div className="flex items-center gap-2">
      <Sparkline points={points} ariaLabel={`Margin trend for ${label ?? 'row'}`} />
      {trend ? <TrendDeltaChip trend={trend} /> : null}
    </div>
  )
}

function costTrendCell(row: PricingRow) {
  if (row.normalization_status === 'unavailable') {
    return <span className="text-tertiary">can't normalize</span>
  }
  const trend = row.cost_trend_yoy
  if (!trend) {
    return <span className="text-role-metadata text-tertiary">Not enough data</span>
  }
  return <CostTrendChip trend={trend} />
}

function priceCell(row: PricingRow, unit: Unit) {
  const price = unit === 'kg' ? row.price_per_kg : row.price_per_lb
  if (price === null) {
    return (
      <span className="text-tertiary" title="No weight UoM and no product weight — can't normalize">
        can't normalize
      </span>
    )
  }
  return fmtMoney(price)
}

function costCell(row: PricingRow, unit: Unit) {
  const cost = unit === 'kg' ? row.cost_per_kg_last : row.cost_per_lb_last
  if (cost == null) return <span className="text-tertiary">—</span>
  return fmtMoney(cost)
}

function fmtQty(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })
}

function fmtMoney(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 4 })
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString()
}
